/**
 * Video overlay variables hooks – parse template variables and predefined odds in HTML strings.
 */
import { isFalsy } from 'shared/utils/common';
import { HORSES_MARKET_TYPE_ALIAS } from 'shared/constants/markets/alias-type-mappings';
import { flattenObj } from 'shared/utils/object-array-helper';

export interface ParsePredefinedOddsParams {
  string_html?: string;
  source_data?: Record<string, unknown>;
  sport_id?: number | string;
}

export interface ParseVariablesParams {
  string_html?: string;
  source_data?: Record<string, unknown>;
}

/**
 * Inspect and replace predefined words that may be variables (e.g. "Winner 1 Odd").
 * Supports racing market type words when sport_id is 20 (horses).
 */
export function parsePredefinedOdds({
  string_html = '',
  source_data = {},
  sport_id,
}: ParsePredefinedOddsParams): string {
  const regex = /((winner|place|show) \d+ odd)/gim;
  let new_html = string_html;

  const { odds = {} } = source_data as { odds?: Record<string, Record<string, unknown>> };
  if (isFalsy(sport_id)) {
    return string_html;
  }

  let mapper: Record<string, number> | undefined;
  if (parseInt(String(sport_id), 10) === 20) {
    mapper = HORSES_MARKET_TYPE_ALIAS;
  }

  if (isFalsy(mapper)) {
    return string_html;
  }

  const matches = [...new Set(string_html.match(regex) || [])];
  for (let i = matches.length - 1; i >= 0; i--) {
    const parts = matches[i]!.split(' ');
    const market_name = parts[0];
    const runner_number = parts[1];
    const mapped = mapper![market_name!.toLowerCase()];
    if (mapped == null) continue;
    const mapped_odds = odds[String(mapped)] as Record<string, unknown> | undefined;
    if (!mapped_odds) continue;
    const mapped_odd_number = mapped_odds[runner_number!];
    if (mapped_odd_number == null) continue;
    new_html = new_html.split(matches[i]).join(String(mapped_odd_number));
  }
  return new_html;
}

/**
 * Inspect and replace all {{ variable }} placeholders with flattened source_data values.
 */
export function parseVariables({
  string_html = '',
  source_data = {},
}: ParseVariablesParams): string {
  const regex = /\{\{(.*?)\}\}/gim;
  let new_html = string_html;

  if (isFalsy(source_data)) {
    return string_html;
  }

  const flatten_source = flattenObj(source_data) as Record<string, unknown>;
  const matches = [...new Set(string_html.match(regex) || [])];
  for (let i = matches.length - 1; i >= 0; i--) {
    const search = matches[i]!.replace('{{', '').replace('}}', '').trim();
    const replace = flatten_source[search];
    if (isFalsy(replace)) continue;
    new_html = new_html.split(matches[i]).join(String(replace));
  }
  return new_html;
}
